/**
 * 模組頁面代碼 : 
 * 頁面說明 : 標題元件
 * @module features/main-frame/Header.jsx
 */

// ----- import from npm modules -----
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';

// ----- import from local actions -----
import { logout } from '../../../redux/actions/AuthActions';

const Header = ({
    userPermissions = [],
    userName = '',
    roleName = '',
    onLogout,
    isLogin = false,
    toggleLoginMessageModal = () => {}, // 預設為空函式避免未傳入時出錯
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [openIndex, setOpenIndex] = useState(null);
    const [navOpen, setNavOpen] = useState(false);
    const navRef = useRef();
    const logoutRef = useRef();

    const [homeUrl, setHomeUrl] = useState('/');
    const menuConfig = ['梯控管理', '系統設定'];

    useEffect(() => {
        if (Array.isArray(userPermissions)) {
            for (const permission of userPermissions) {
                if (permission.children) {
                    for (const child of permission.children) {
                        if (child.defaultPage === 1 && child.flgRead === 1) {
                            const navigatePath = `/module/${child.navigatePath}`.replace(/\/+/g, '/');
                            setHomeUrl(navigatePath);
                            return;
                        }
                    }
                }
            }
        }
    }, [userPermissions]);

    // 點擊外部自動收合選單
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                navRef.current &&
                !navRef.current.contains(event.target) &&
                !event.target.closest('[data-js-gs18-pulldown-open-global-menu]')
            ) {
                setOpenIndex(null);
                setNavOpen(false);
                toggleLoginMessageModal(false);
                document.body.classList.remove('gs18-HeaderFog');
            }
            if (logoutRef.current && logoutRef.current.contains(event.target)) {
                // 執行登出操作
                handleLogout(event);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 螢幕尺寸變化時自動收合
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 980) {
                setNavOpen(false);
                setOpenIndex(null);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // flyout header（滾動時隱藏/顯示 header）
    useEffect(() => {
        const headerEl = document.querySelector('.gs18-Header');
        if (!headerEl) return;

        headerEl.classList.add('is-flyout');
        let lastScrollTop = 0;
        let scrollTimer = null;

        const onScroll = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            if (scrollTop > 140) {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                    if (scrollTop > lastScrollTop) {
                        headerEl.classList.add('is-hidden');
                    } else {
                        headerEl.classList.remove('is-hidden');
                    }
                    lastScrollTop = scrollTop;
                }, 300);
            } else {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                    headerEl.classList.remove('is-hidden');
                    lastScrollTop = scrollTop;
                }, 300);
            }
        };

        window.addEventListener('scroll', onScroll);
        onScroll();

        return () => {
            window.removeEventListener('scroll', onScroll);
            clearTimeout(scrollTimer);
        };
    }, []);

    useEffect(() => {
        // 如果是 RWD 瀏覽器，則根據 navOpen 狀態添加或移除 gs18-HeaderFog 類別
        if (window.innerWidth <= 980) {
            if (navOpen) {
                document.body.classList.add('gs18-HeaderFog');
            } else {
                document.body.classList.remove('gs18-HeaderFog');
            }
        } else { // 如果是 PC 瀏覽器，則始終移除 gs18-HeaderFog 類別
            document.body.classList.remove('gs18-HeaderFog');
        }
    }, [navOpen]);

    const handleMenuToggle = (idx) => {
        if (!isLogin) { toggleLoginMessageModal(true); return; }
        if (window.innerWidth <= 980) {
            // RWD: 展開/收合下拉選單
            setOpenIndex(openIndex === idx ? null : idx);
        } else {
            // PC: 切換 is-drop 樣式
            setOpenIndex(openIndex === idx ? null : idx);
            document.body.classList.add('gs18-HeaderFog');
        }
    };

    const handleCloseMenu = () => {
        setOpenIndex(null);
        setNavOpen(false);
        document.body.classList.remove('gs18-HeaderFog');
    }

    // 根據權限建立選單內容
    const permittedPages = useMemo(() => {
        const map = {};
        userPermissions.forEach((perm) => {
            if (!perm.children) return;
            map[perm.moduleName] = perm.children
                .filter(child => child.flgRead === 1)
                .map((child, idx) => {
                    const path = `/module/${child.navigatePath}`.replace(/\/+/g, '/');
                    return (
                        <li className="nav-item" key={idx}>
                            <NavLink
                                className="nav-link"
                                to={path}
                                onClick={() => {
                                    document.body.classList.remove('gs18-HeaderFog');
                                    setNavOpen(false);
                                    setOpenIndex(null);
                                }}
                            >
                                {child.moduleName}
                            </NavLink>
                        </li>
                    );
                });
        });
        return map;
    }, [userPermissions]);

    // 判斷目前路徑是否屬於某個 menu 群組
    const activeMenuName = useMemo(() => {
        for (const perm of userPermissions) {
            if (!perm.children) continue;
            for (const child of perm.children) {
                const path = `/module/${child.navigatePath}`.replace(/\/+/g, '/');
                if (location.pathname.startsWith(path)) {
                    return perm.moduleName;
                }
            }
        }
        return null;
    }, [userPermissions, location.pathname]);

    // 登出處理
    const handleLogout = (e) => {
        e.preventDefault();
        console.log('Logging out...');
        document.body.classList.remove('gs18-HeaderFog'); // 移除遮罩
        onLogout().then(() => {
            setHomeUrl('/'); // 登出後重設首頁 URL
            navigate('/Login');
        });
    };

    return (
        <header className="gs18-Header gs18-Header--MinInteraction gs18-Header--withme" role="banner" data-js-gs18-header>
            <div className="gs18-Header__Inner">
                <h1 className="gs18-Header__Logo">
                    <NavLink to={isLogin ? homeUrl : '/'} className="nav-link" aria-current="page">
                        <img
                            src="/gws_template_responsive/assets_gws_template_responsive/img/me01_logo.svg"
                            alt="MITSUBISHI ELECTRIC 台灣三菱電梯股份有限公司"
                        />
                    </NavLink>
                </h1>
                <div className={`header__user${isLogin ? (navOpen ? ' is-open-small' : '') : ' no-user'}`}
                    data-js-gs18-pulldown-open-global-menu>
                    {isLogin && (
                    <div className="d-flex justify-content-center align-items-center gap-2 h-100">
                        <div className="fs-6 text-nowrap">{userName}</div>
                        <div className="fs-6 text-nowrap">({roleName})</div>
                        <a
                            className="btn btn-outline-secondary btn-sm d-flex justify-content-center align-items-center text-nowrap"
                            ref={logoutRef}
                            href="#"
                            aria-label="登出"
                        >
                            登出
                        </a>
                    </div>
                    )}
                </div>
                <a
                    className={`gs18-Header__Hamburger${navOpen ? ' is-open-small' : ''}`}
                    data-js-gs18-pulldown-open-global-menu
                    href="#"
                    aria-label="Open menu"
                    onClick={e => {
                        e.preventDefault();
                        setNavOpen(!navOpen);
                    }}
                >
                    <img src="/gws_template_responsive/assets_gws_template_responsive/default/img/icon/Menu.svg" alt="≡ Open menu." />
                </a>
                <nav className={`gs18-HeaderNav${navOpen ? (isLogin ? ' is-nav-open is-open-small' : ' is-nav-open is-open-small no-user') : ''}`} ref={navRef}>
                    <ul className="gs18-HeaderNav__Menu">

                        {menuConfig.map((menu, idx) => (

                            <li className={`gs18-HeaderNav__Item${openIndex === idx ? ' current' : ''}`} data-url="stock" key={menu}>
                                <a
                                    href="#"
                                    className={
                                        `gs18-HeaderNav__Text` +
                                        (window.innerWidth > 980 && openIndex === idx ? ' is-drop is-open' : '') +
                                        (window.innerWidth <= 980 && openIndex === idx ? ' is-opened' : '')
                                    }
                                    data-js-gs18-pulldown-open-menu=""
                                    aria-expanded={openIndex === idx}
                                    aria-controls={`pulldown-${idx}`}
                                    role="button"
                                    tabIndex={idx}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleMenuToggle(idx);
                                    }}
                                >
                                    {menu}
                                </a>

                                <div
                                    id={`pulldown-${idx}`}
                                    className={`gs18-HeaderPulldown${openIndex === idx ? ' is-opened' : ''}`}
                                    style={{ display: openIndex === idx ? 'block' : 'none' }}
                                >
                                    <div className="gh-wrap">
                                        <div className="gh-lv1"><dl className="gh-lv1__title"><dt>{menu}</dt></dl></div>
                                        <div className="gh-lv2">
                                            <div className="gh-scroll">
                                                <ul className="gh-list">{isLogin && permittedPages[menu]}</ul>
                                            </div>
                                        </div>
                                        <div className="gh-lv3"><div className="gh-scroll"></div></div>
                                        <button className="gh-close" onClick={handleCloseMenu} aria-label="關閉選單">CLOSE</button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </nav>
                {/* /.gs18-Header__Inner */}
            </div>
            {/* /.gs18-Header */}
        </header>
    );
};

Header.propTypes = {
    className: PropTypes.string,
    forwardedRef: PropTypes.object,
    userPermissions: PropTypes.array,
    userName: PropTypes.string,
    roleName: PropTypes.string,
    onLogout: PropTypes.func.isRequired,
    isLogin: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        userPermissions: state.access.userPermissions,
        userName: state.auth.userName,
        roleName: state.auth.roleName,
        isLogin: state.auth.isLogin,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onLogout: () => {
            return dispatch(logout());
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Header);